import React, { useState, useEffect } from 'react';
import { Save, Building2, MapPin, Phone, Mail, Globe, Plus, Trash2, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import { footerSettingsAPI } from '../../services/api';

const FooterSettingsTab = () => {
  const [settings, setSettings] = useState({
    company_name: 'Gmed Srl',
    tagline_it: '',
    tagline_en: '',
    address_line1: '',
    address_line2: '',
    partita_iva: '',
    codice_fiscale: '',
    codice_sdi: '',
    capitale_sociale: '',
    rea: '',
    phone: '',
    fax: '',
    email: '',
    pec: '',
    facebook_url: '',
    twitter_url: '',
    linkedin_url: '',
    instagram_url: '',
    custom_fields: ''
  });
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await footerSettingsAPI.get();
      setSettings(response.data);
      // Parse custom fields
      if (response.data.custom_fields) {
        try {
          setCustomFields(JSON.parse(response.data.custom_fields));
        } catch {
          setCustomFields([]);
        }
      }
    } catch (error) {
      toast.error('Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Serialize custom fields
      const dataToSave = {
        ...settings,
        custom_fields: customFields.length > 0 ? JSON.stringify(customFields) : null
      };
      
      await footerSettingsAPI.update(dataToSave);
      toast.success('Impostazioni footer salvate!');
    } catch (error) {
      toast.error(
        error.response?.status === 401
          ? 'Sessione scaduta: accedi di nuovo e riprova a salvare'
          : error.response?.data?.detail || 'Errore nel salvare le impostazioni'
      );
    } finally {
      setSaving(false);
    }
  };

  const addCustomField = () => {
    setCustomFields([...customFields, { label: '', value: '' }]);
  };

  const updateCustomField = (index, field, value) => {
    const updated = [...customFields];
    updated[index][field] = value;
    setCustomFields(updated);
  };

  const removeCustomField = (index) => {
    setCustomFields(customFields.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <Card className="glass-card">
        <CardContent className="py-12 text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Company Info Card */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Informazioni Aziendali
          </CardTitle>
          <CardDescription>Dati principali dell'azienda visualizzati nel footer</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company_name">Nome Azienda</Label>
              <Input
                id="company_name"
                value={settings.company_name}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                className="mt-2"
                data-testid="footer-company-name"
              />
            </div>
            <div>
              <Label htmlFor="email">Email Generale</Label>
              <Input
                id="email"
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                placeholder="comunicazioni@gmed.it"
                className="mt-2"
                data-testid="footer-email"
              />
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="tagline_it">Slogan (Italiano)</Label>
              <Input
                id="tagline_it"
                value={settings.tagline_it}
                onChange={(e) => setSettings({ ...settings, tagline_it: e.target.value })}
                placeholder="Innovazione e qualità nel settore medicale"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="tagline_en">Slogan (Inglese)</Label>
              <Input
                id="tagline_en"
                value={settings.tagline_en}
                onChange={(e) => setSettings({ ...settings, tagline_en: e.target.value })}
                placeholder="Innovation and quality in the medical sector"
                className="mt-2"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address Card */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Indirizzo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="address_line1">Indirizzo (riga 1)</Label>
              <Input
                id="address_line1"
                value={settings.address_line1}
                onChange={(e) => setSettings({ ...settings, address_line1: e.target.value })}
                placeholder="Via Selvamaggio, 19"
                className="mt-2"
                data-testid="footer-address1"
              />
            </div>
            <div>
              <Label htmlFor="address_line2">Città e CAP (riga 2)</Label>
              <Input
                id="address_line2"
                value={settings.address_line2}
                onChange={(e) => setSettings({ ...settings, address_line2: e.target.value })}
                placeholder="53034 - Colle di Val d'Elsa (SI)"
                className="mt-2"
                data-testid="footer-address2"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Info Card */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Contatti
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Telefono</Label>
              <Input
                id="phone"
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                placeholder="+39 0577 123456"
                className="mt-2"
                data-testid="footer-phone"
              />
            </div>
            <div>
              <Label htmlFor="fax">Fax</Label>
              <Input
                id="fax"
                value={settings.fax}
                onChange={(e) => setSettings({ ...settings, fax: e.target.value })}
                placeholder="+39 0577 654321"
                className="mt-2"
                data-testid="footer-fax"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="pec">PEC</Label>
            <Input
              id="pec"
              type="email"
              value={settings.pec}
              onChange={(e) => setSettings({ ...settings, pec: e.target.value })}
              placeholder="gmed@pec.it"
              className="mt-2"
              data-testid="footer-pec"
            />
          </div>
        </CardContent>
      </Card>

      {/* Legal Info Card */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Dati Legali e Fiscali
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="partita_iva">Partita IVA</Label>
              <Input
                id="partita_iva"
                value={settings.partita_iva}
                onChange={(e) => setSettings({ ...settings, partita_iva: e.target.value })}
                placeholder="01203470529"
                className="mt-2"
                data-testid="footer-piva"
              />
            </div>
            <div>
              <Label htmlFor="codice_fiscale">Codice Fiscale</Label>
              <Input
                id="codice_fiscale"
                value={settings.codice_fiscale}
                onChange={(e) => setSettings({ ...settings, codice_fiscale: e.target.value })}
                placeholder="01203470529"
                className="mt-2"
                data-testid="footer-cf"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="codice_sdi">Codice SDI</Label>
              <Input
                id="codice_sdi"
                value={settings.codice_sdi}
                onChange={(e) => setSettings({ ...settings, codice_sdi: e.target.value })}
                placeholder="KRRH6B9"
                className="mt-2"
                data-testid="footer-sdi"
              />
            </div>
            <div>
              <Label htmlFor="capitale_sociale">Capitale Sociale</Label>
              <Input
                id="capitale_sociale"
                value={settings.capitale_sociale}
                onChange={(e) => setSettings({ ...settings, capitale_sociale: e.target.value })}
                placeholder="€ 10.000,00 i.v."
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="rea">REA</Label>
              <Input
                id="rea"
                value={settings.rea}
                onChange={(e) => setSettings({ ...settings, rea: e.target.value })}
                placeholder="SI-123456"
                className="mt-2"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Social Links Card */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Social Media
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="facebook_url">Facebook</Label>
              <Input
                id="facebook_url"
                value={settings.facebook_url}
                onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })}
                placeholder="https://facebook.com/gmed"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="linkedin_url">LinkedIn</Label>
              <Input
                id="linkedin_url"
                value={settings.linkedin_url}
                onChange={(e) => setSettings({ ...settings, linkedin_url: e.target.value })}
                placeholder="https://linkedin.com/company/gmed"
                className="mt-2"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="twitter_url">Twitter / X</Label>
              <Input
                id="twitter_url"
                value={settings.twitter_url}
                onChange={(e) => setSettings({ ...settings, twitter_url: e.target.value })}
                placeholder="https://twitter.com/gmed"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="instagram_url">Instagram</Label>
              <Input
                id="instagram_url"
                value={settings.instagram_url}
                onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
                placeholder="https://instagram.com/gmed"
                className="mt-2"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Custom Fields Card */}
      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Campi Personalizzati
              </CardTitle>
              <CardDescription>Aggiungi informazioni aggiuntive al footer</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addCustomField}>
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Campo
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {customFields.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nessun campo personalizzato. Clicca "Aggiungi Campo" per aggiungerne uno.
            </p>
          ) : (
            <div className="space-y-3">
              {customFields.map((field, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <div className="flex-1">
                    <Input
                      value={field.label}
                      onChange={(e) => updateCustomField(index, 'label', e.target.value)}
                      placeholder="Etichetta (es. Registro Imprese)"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      value={field.value}
                      onChange={(e) => updateCustomField(index, 'value', e.target.value)}
                      placeholder="Valore (es. SI-123456)"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeCustomField(index)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Alert */}
      <Alert className="border-primary/20 bg-primary/5">
        <Info className="h-4 w-4 text-primary" />
        <AlertDescription className="ml-2">
          Le modifiche saranno visibili nel footer del sito dopo il salvataggio. 
          I campi lasciati vuoti non verranno mostrati.
        </AlertDescription>
      </Alert>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg" data-testid="save-footer-settings">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Salvataggio...' : 'Salva Impostazioni Footer'}
        </Button>
      </div>
    </div>
  );
};

export default FooterSettingsTab;
